import { Popover } from "antd";
import { PopoverTrigger } from "../../../enums/PopoverEnums";
import styles from "./JobDatabaseTable.module.scss";
import MoreEllipsis from "../../../assets/images/ellipsisListing.svg";
import ArchiveIcon from "../../../assets/images/make-primary-document-icon.svg";
import LockedIcon from "../../../assets/images/lockedIcon.svg";
import BlockIcon from "../../../assets/images/lockIconOutline.svg";
import UnblockIcon from "../../../assets/images/unlockIconOutline.svg";
import { isEmptyValue } from "../../../utils/emptyStringCheck";
import { formatNumber } from "../../../utils/numberFormatterUtil";
import clsx from "clsx";
import { DispatchStatus } from "../../../enums/DispatchStatusEnum";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import DescriptionCell from "../../../reusable-components/DescriptionCell";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { TableSortTypes } from "../../../enums/ViewListingTypes";
import { getTableSortOrder } from "../../../utils/getTableSortOrder";

export const JobSortColumns = {
  JOB: "jobNumber",
  JOB_NAME: "jobName",
  CUSTOMER_NAME: "companyName",
  JOB_TYPE: "jobType",
  EQUIPMENT: "equipment",
  MATERIAL_CATEGORY: "materialCategory",
  MATERIAL_TYPE: "materialType",
  MATERIAL: "material",
  SALES_PERSON: "salesPerson",
  PROJECT_REVENUE: "revenue",
  COGS: "cogs",
  GROSS_PROFIT: "grossProfit",
  GROSS_PROFIT_PERCENTAGE: "grossProfitPercentage",
};

export const getDatabaseColumns = ({
  handleArchiveConfirmOpen,
  handleCustomerNavigate,
  handleBlockUnblockConfirm,
  jobSortValues: filterValues,
}) => {
  const { sortBy, sortOrder } = filterValues || {};
  return [
    {
      title: "JOB",
      dataIndex: "jobNumber",
      key: JobSortColumns.JOB,
      width: "60px",
      align: "center",
      fixed: "left",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.JOB),
      render: (jobNumber, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {jobNumber}
        </span>
      ),
    },
    {
      title: "JOB NAME",
      dataIndex: "jobName",
      key: JobSortColumns.JOB_NAME,
      width: "280px",
      align: "center",
      fixed: "left",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.JOB_NAME),

      render: (jobName, record) => (
        <>
          <ConditionalRender visible={!!record?.isBlocked}>
            <img
              className={styles.blockedIcon}
              src={LockedIcon}
              alt={"blockedJob"}
            />
          </ConditionalRender>
          <span
            className={clsx({
              [styles.dispatchPending]:
                record?.jobStatus === DispatchStatus?.PENDING,
              [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
            })}
          >
            {jobName}
          </span>
        </>
      ),
    },
    {
      title: "CUSTOMER NAME",
      dataIndex: "companyName",
      key: JobSortColumns.CUSTOMER_NAME,
      width: "350px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.CUSTOMER_NAME,
      ),

      render: (customerName, record) => (
        <ConditionalRender visible={!!customerName} fallback={"-"}>
          <span
            onClick={() =>
              handleCustomerNavigate(customerName, record?.companyId)
            }
            className={styles.customerName}
          >
            {customerName}
          </span>
        </ConditionalRender>
      ),
    },
    {
      title: "JOB TYPE",
      dataIndex: "jobType",
      key: JobSortColumns.JOB_TYPE,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.JOB_TYPE),

      render: (jobType, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(jobType) ? "-" : jobType}
        </span>
      ),
    },
    {
      title: "EQUIPMENT",
      dataIndex: "equipment",
      key: JobSortColumns.EQUIPMENT,
      width: "180px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.EQUIPMENT),

      render: (truckType, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          <ConditionalRender
            visible={truckType?.length >= 1 && !!truckType?.[0]}
            fallback={"-"}
          >
            <DescriptionCell text={truckType?.join(", ")} />
          </ConditionalRender>
        </span>
      ),
    },
    {
      title: "MATERIAL CATEGORY",
      dataIndex: "materialCategory",
      key: JobSortColumns.MATERIAL_CATEGORY,
      width: "180px",
      align: "center",
      ellipsis: true,
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.MATERIAL_CATEGORY,
      ),
      render: (materialCatergory, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          <ConditionalRender
            visible={materialCatergory?.length > 0}
            fallback={"-"}
          >
            <DescriptionCell text={materialCatergory?.join(", ")} />
          </ConditionalRender>
        </span>
      ),
    },
    {
      title: "MATERIAL TYPE",
      dataIndex: "materialType",
      key: JobSortColumns.MATERIAL_TYPE,
      width: "180px",
      align: "center",
      ellipsis: true,
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.MATERIAL_TYPE,
      ),
      render: (materialType, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          <ConditionalRender visible={materialType?.length > 0} fallback={"-"}>
            <DescriptionCell text={materialType?.join(", ")} />
          </ConditionalRender>
        </span>
      ),
    },
    {
      title: "MATERIAL",
      dataIndex: "materialName",
      key: JobSortColumns.MATERIAL,
      width: "180px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.MATERIAL),

      render: (material, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          <ConditionalRender visible={material?.length > 0} fallback={"-"}>
            <DescriptionCell text={material?.join(", ")} />
          </ConditionalRender>
        </span>
      ),
    },
    {
      title: "SALES PERSON",
      dataIndex: "salesPerson",
      key: JobSortColumns.SALES_PERSON,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.SALES_PERSON,
      ),

      render: (salesPerson, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(salesPerson) ? "-" : salesPerson}
        </span>
      ),
    },
    {
      title: "PROJECT REVENUE",
      dataIndex: "revenue",
      key: JobSortColumns.PROJECT_REVENUE,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.PROJECT_REVENUE,
      ),
      render: (revenue, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(revenue)
            ? "-"
            : `$${formatNumber(Number(revenue).toFixed(2))}`}
        </span>
      ),
    },
    {
      title: "COGS",
      dataIndex: "cogs",
      key: JobSortColumns.COGS,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(sortBy, sortOrder, JobSortColumns.COGS),

      render: (cogs, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(cogs)
            ? "-"
            : `$${formatNumber(Number(cogs).toFixed(2))}`}
        </span>
      ),
    },
    {
      title: "GROSS PROFIT",
      dataIndex: "gross",
      key: JobSortColumns.GROSS_PROFIT,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.GROSS_PROFIT,
      ),
      render: (gross, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(gross?.profit)
            ? "-"
            : `$${formatNumber(Number(gross?.profit).toFixed(2))}`}
        </span>
      ),
    },
    {
      title: "GP%",
      dataIndex: "gross",
      key: JobSortColumns.GROSS_PROFIT_PERCENTAGE,
      width: "170px",
      align: "center",
      sorter: true,
      sortOrder: getTableSortOrder(
        sortBy,
        sortOrder,
        JobSortColumns.GROSS_PROFIT_PERCENTAGE,
      ),
      render: (gross, record) => (
        <span
          className={clsx({
            [styles.dispatchPending]:
              record?.jobStatus === DispatchStatus?.PENDING,
            [styles.closedJob]: record?.jobStatus === DispatchStatus?.CLOSED,
          })}
        >
          {isEmptyValue(gross?.profitPercentage)
            ? ""
            : `${Number(gross?.profitPercentage).toFixed(2)}%`}
        </span>
      ),
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: "40px",
      align: "center",
      fixed: "right",
      render: (_, record) => {
        return (
          <Popover
            trigger={PopoverTrigger.HOVER}
            placement={PlacementPositions?.LEFT}
            content={
              <div className={styles.actionsContainer}>
                <span onClick={() => handleArchiveConfirmOpen(record)}>
                  <img src={ArchiveIcon} alt={"archive"} />
                  <span className={styles.actionOptionText}>{"Archive"}</span>
                </span>
                <ConditionalRender visible={!!!record?.isBlocked}>
                  <span onClick={() => handleBlockUnblockConfirm(record)}>
                    <img src={BlockIcon} alt={"block"} />
                    <span className={styles.actionOptionText}>{"Block"}</span>
                  </span>
                </ConditionalRender>
                <ConditionalRender visible={!!record?.isBlocked}>
                  <span onClick={() => handleBlockUnblockConfirm(record)}>
                    <img src={UnblockIcon} alt={"unblock"} />
                    <span className={styles.actionOptionText}>{"Unblock"}</span>
                  </span>
                </ConditionalRender>
              </div>
            }
          >
            <span className={styles.editEllipsis}>
              <img src={MoreEllipsis} />
            </span>
          </Popover>
        );
      },
    },
  ];
};

export const getTableFooter = () => {
  return (
    <div className={styles.footerContainer}>
      <div className={styles.keyCode}>
        <div className={styles.dispatchPendingKey} />
        <span>{"Dispatch Not Approved"}</span>
      </div>
      <div className={styles.keyCode}>
        <div className={styles.dispatchedJobKey} />
        <span>{"Dispatch Approved"}</span>
      </div>
      <div className={styles.keyCode}>
        <div className={styles.closedJobKey} />
        <span>{"Job is Closed"}</span>
      </div>
    </div>
  );
};

export const JobDatabaseTableConsts = {
  NO_RECORDS: "No records available",
  NO_RECORDS_FOUND: "No records found",
  NO_ACCESS_CUSTOMER_DB: "Access required to view Customer Profile",
  BLOCK_TITLE: "Block Job",
  BLOCK_MSG: "Are you sure you want to block this job ?",
  UNBLOCK_TITLE: "Block Job",
  UNBLOCK_MSG: "Are you sure you want to unblock this job ?",
};
